import { useEffect, useRef, useState, type RefObject } from 'react';
import { FIT_LAYOUT_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import type { ExposureRow } from '@/store/heatmapStore';
import type { HeatmapScrollOptions } from '../HeatmapTable';

// Matches the heatmap-flash animation in index.css.
const FLASH_DURATION_MS = 1400;

interface UseHeatmapCenteringArgs {
  tableRef: RefObject<HTMLTableElement | null>;
  scrollerRef: RefObject<HTMLDivElement | null>;
  displayRows: readonly ExposureRow[];
}

/**
 * Centers a heatmap row the way the reference does: in the fit layout only the heatmap's
 * own scroller moves; in stacked/phone layouts the page scrolls only for an explicit
 * spot/king click, never on load or view changes, so the toolbar stays in view.
 */
export function useHeatmapCentering({
  tableRef,
  scrollerRef,
  displayRows,
}: UseHeatmapCenteringArgs) {
  const fits = useMediaQuery(FIT_LAYOUT_MEDIA_QUERY);
  const [flashStrike, setFlashStrike] = useState<number | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimeoutRef.current != null) {
        clearTimeout(flashTimeoutRef.current);
      }
    },
    [],
  );

  const flash = (strike: number | null) => {
    // Keyed by strike so a shifted strike window never replays the flash on another row.
    setFlashStrike(null);
    requestAnimationFrame(() => setFlashStrike(strike));
    if (flashTimeoutRef.current != null) {
      clearTimeout(flashTimeoutRef.current);
    }
    flashTimeoutRef.current = setTimeout(() => setFlashStrike(null), FLASH_DURATION_MS);
  };

  const centerRow = (
    rowIndex: number | null,
    inline: boolean,
    { automatic = false }: HeatmapScrollOptions = {},
  ) => {
    if (rowIndex == null || (automatic && !fits)) {
      return;
    }
    const target = inline
      ? tableRef.current?.querySelector<HTMLElement>('td[data-king]')
      : tableRef.current?.tBodies[0]?.rows[rowIndex];
    if (target == null) {
      return;
    }

    const scroller = scrollerRef.current;
    if (fits && scroller != null) {
      const box = scroller.getBoundingClientRect();
      const cell = target.getBoundingClientRect();
      scroller.scrollTo({
        top: scroller.scrollTop + cell.top - box.top - box.height / 2 + cell.height / 2,
        left: inline
          ? scroller.scrollLeft + cell.left - box.left - box.width / 2 + cell.width / 2
          : scroller.scrollLeft,
        behavior: automatic ? 'auto' : 'smooth',
      });
    } else {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: inline ? 'center' : 'nearest',
      });
    }

    if (!automatic) {
      flash(displayRows[rowIndex]?.strike ?? null);
    }
  };

  return { flashStrike, centerRow };
}
